import { redactTier2Record, type TimelineDisplayScope } from "./tier2Redaction";

export type AuditFieldDiff = {
  field: string;
  before: unknown;
  after: unknown;
  changed: boolean;
};

/** Inline value rendering aligned with `server/audit/auditDiff.ts` `formatVal`. */
export function formatAuditDisplayVal(v: unknown): string {
  if (v === null || v === undefined) return "∅";
  if (typeof v === "string") {
    const trimmed = v.length > 48 ? `${v.slice(0, 45)}…` : v;
    return JSON.stringify(trimmed);
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const json = JSON.stringify(v);
    return json.length > 48 ? `${json.slice(0, 45)}…` : json;
  } catch {
    return String(v);
  }
}

function equalsForAudit(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === "number" && typeof b === "string") return Number(b) === a;
  if (typeof b === "number" && typeof a === "string") return Number(a) === b;
  if (typeof a === "string" && typeof b === "string") return a.trim() === b.trim();
  return false;
}

export function buildAuditFieldDiffs(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  scope: TimelineDisplayScope
): AuditFieldDiff[] {
  const b = redactTier2Record(before ?? null, scope) ?? {};
  const a = redactTier2Record(after ?? null, scope) ?? {};
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].sort();
  return keys.map((field) => {
    const beforeVal = b[field];
    const afterVal = a[field];
    return {
      field,
      before: beforeVal,
      after: afterVal,
      changed: !equalsForAudit(beforeVal, afterVal),
    };
  });
}

export function timelineEntryHasSnapshots(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined
): boolean {
  const hasBefore = before != null && Object.keys(before).length > 0;
  const hasAfter = after != null && Object.keys(after).length > 0;
  return hasBefore || hasAfter;
}
